import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Skeleton,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SendIcon from "@mui/icons-material/Send";
import { settingsApi, AppSettings } from "../../services/api";
import { authApi } from "../../services/auth";

export default function SettingsPage() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [taxRate, setTaxRate] = useState("");
  const [ccFeeRate, setCcFeeRate] = useState("");
  const [boothCommRate, setBoothCommRate] = useState("");
  const [boothFee, setBoothFee] = useState("");
  const [rentOverhead, setRentOverhead] = useState("");

  // Invite state
  const [inviteEmail, setInviteEmail] = useState("");
  const [invites, setInvites] = useState<any[]>([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const loadInvites = () => {
    authApi.listInvites().then((res) => setInvites(res.data)).catch(() => {});
  };

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => {
        const s = res.data;
        setSettings(s);
        // Convert decimal to percentage for display
        setTaxRate((Number(s.tax_rate) * 100).toFixed(2));
        setCcFeeRate((Number(s.credit_card_fee_rate) * 100).toFixed(2));
        setBoothCommRate((Number(s.booth_commission_rate) * 100).toFixed(2));
        setBoothFee(s.booth_fee);
        setRentOverhead(s.rent_overhead);
      })
      .catch(() => setError("Failed to load settings."))
      .finally(() => setLoading(false));

    loadInvites();
  }, []);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviteLoading(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      await authApi.invite(inviteEmail);
      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      loadInvites();
    } catch (err: any) {
      setInviteError(err.response?.data?.detail || "Failed to send invitation.");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await settingsApi.update({
        tax_rate: (Number(taxRate) / 100).toFixed(4),
        credit_card_fee_rate: (Number(ccFeeRate) / 100).toFixed(4),
        booth_commission_rate: (Number(boothCommRate) / 100).toFixed(4),
        booth_fee: boothFee,
        rent_overhead: rentOverhead,
      });
      setSuccess(true);
    } catch {
      setError("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Settings</Typography>
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Tax & Fees */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tax & Fees
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                These rates are used to calculate net profit on sold items.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Sales Tax Rate"
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", max: "100", inputMode: "decimal" }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                  }}
                  helperText="e.g. 8.25 for 8.25%"
                />

                <TextField
                  label="Credit Card Fee Rate"
                  type="number"
                  value={ccFeeRate}
                  onChange={(e) => setCcFeeRate(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", max: "100", inputMode: "decimal" }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                  }}
                  helperText="e.g. 3.00 for 3%"
                />

                <TextField
                  label="Booth Commission Rate"
                  type="number"
                  value={boothCommRate}
                  onChange={(e) => setBoothCommRate(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", max: "100", inputMode: "decimal" }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                  }}
                  helperText="e.g. 10.00 for 10% — the cut the booth takes from each sale"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Overhead */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Overhead
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fixed monthly costs that are deducted from net profit.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Booth Fee"
                  type="number"
                  value={boothFee}
                  onChange={(e) => setBoothFee(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                  }}
                  helperText="Monthly booth rental fee"
                />

                <TextField
                  label="Rent / Overhead"
                  type="number"
                  value={rentOverhead}
                  onChange={(e) => setRentOverhead(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                  }}
                  helperText="Other monthly rent or overhead costs"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ minWidth: 200 }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </Grid>

      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess(false)}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}
